import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Logo from 'components/Logo';
import useSEO from 'hooks/useSEO';

const EFFECTIVE_DATE = 'March 1, 2025';
const COMPANY       = 'Safe Dividend Tracker';
const DOMAIN        = 'safedividendtracker.com';
const EMAIL_PRIVACY = 'privacy@safedividendtracker.com';
const EMAIL_SUPPORT = 'support@safedividendtracker.com';

const sections = [
  { id: 'introduction',      label: 'Introduction' },
  { id: 'information',       label: 'Information We Collect' },
  { id: 'use',               label: 'How We Use Your Data' },
  { id: 'sharing',           label: 'Sharing & Disclosure' },
  { id: 'retention',         label: 'Data Retention' },
  { id: 'security',          label: 'Security' },
  { id: 'cookies',           label: 'Cookies & Tracking' },
  { id: 'rights',            label: 'Your Rights' },
  { id: 'children',          label: 'Children\'s Privacy' },
  { id: 'changes',           label: 'Policy Changes' },
  { id: 'contact',           label: 'Contact Us' },
];

function SectionAnchor({ id }) {
  return <span id={id} style={{ scrollMarginTop: 80 }} />;
}

function SectionTitle({ children }) {
  return (
    <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1E293B', fontFamily: 'var(--font-heading)', marginBottom: 16, marginTop: 40 }}>
      {children}
    </h2>
  );
}

function SubTitle({ children }) {
  return (
    <h3 style={{ fontSize: 16, fontWeight: 600, color: '#334155', fontFamily: 'var(--font-caption)', marginBottom: 10, marginTop: 24 }}>
      {children}
    </h3>
  );
}

function Para({ children }) {
  return (
    <p style={{ fontSize: 15, color: '#475569', lineHeight: 1.8, marginBottom: 14 }}>
      {children}
    </p>
  );
}

function BulletList({ items }) {
  return (
    <ul style={{ paddingLeft: 20, marginBottom: 14 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 15, color: '#475569', lineHeight: 1.75, marginBottom: 6, listStyleType: 'disc' }}>{item}</li>
      ))}
    </ul>
  );
}

function InfoBox({ icon, title, children }) {
  return (
    <div style={{ background: 'rgba(46,125,50,0.06)', border: '1px solid rgba(46,125,50,0.18)', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Icon name={icon} size={16} color="#2E7D32" />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#2E7D32', fontFamily: 'var(--font-caption)' }}>{title}</span>
      </div>
      <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.7, margin: 0 }}>{children}</p>
    </div>
  );
}

export default function PrivacyPolicyPage() {
  useSEO({
    title: 'Privacy Policy',
    description: 'Read Safe Dividend Tracker\'s privacy policy. Learn how we collect, use, and protect your personal data when you use our dividend portfolio tracking platform.',
    canonical: '/privacy-policy',
  });

  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    const handleScroll = () => {
      for (const s of [...sections].reverse()) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(s.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>

      {/* ── Top nav ────────────────────────────────────────────────────────── */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '0 48px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <Logo size={36} />
          <span style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', fontFamily: 'var(--font-heading)' }}>Safe Dividend Tracker</span>
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          {[['/#features', 'Features'], ['/#pricing', 'Pricing'], ['/blog', 'Blog'], ['/#faq', 'FAQ'], ['/contact', 'Contact'], ['/privacy-policy', 'Privacy']].map(([href, label]) => (
            <Link key={label} to={href} style={{ fontSize: 14, color: label === 'Privacy' ? '#2E7D32' : '#64748B', fontWeight: label === 'Privacy' ? 700 : 500, fontFamily: 'var(--font-caption)', textDecoration: 'none' }}>{label}</Link>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/login" style={{ fontSize: 14, color: '#475569', fontWeight: 500, textDecoration: 'none', padding: '8px 16px', border: '1px solid #E2E8F0', borderRadius: 8, fontFamily: 'var(--font-caption)' }}>Sign In</Link>
          <Link to="/register" style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#2E7D32,#388E3C)', padding: '8px 18px', borderRadius: 8, textDecoration: 'none', fontFamily: 'var(--font-caption)', boxShadow: '0 2px 8px rgba(46,125,50,0.3)' }}>Get Started Free</Link>
        </div>
      </header>

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(180deg, #EEF2FF 0%, #F8FAFC 100%)', borderBottom: '1px solid #E2E8F0' }}>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" style={{ fontSize: 13, color: '#64748B', fontFamily: 'var(--font-caption)' }} className="hover:text-slate-700">Home</Link>
            <Icon name="ChevronRight" size={14} color="#94A3B8" />
            <span style={{ fontSize: 13, color: '#1E293B', fontFamily: 'var(--font-caption)' }}>Privacy Policy</span>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 mt-1" style={{ background: 'rgba(46,125,50,0.1)', border: '1px solid rgba(46,125,50,0.2)' }}>
              <Icon name="Shield" size={28} color="#2E7D32" />
            </div>
            <div>
              <h1 style={{ fontSize: 34, fontWeight: 800, color: '#0F172A', fontFamily: 'var(--font-heading)', lineHeight: 1.2 }}>Privacy Policy</h1>
              <p style={{ fontSize: 14, color: '#64748B', marginTop: 6, fontFamily: 'var(--font-caption)' }}>
                Effective date: <strong>{EFFECTIVE_DATE}</strong> · Last reviewed: March 2025
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body layout ──────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6 py-12 flex gap-12">

        {/* Sidebar TOC */}
        <aside className="hidden lg:block flex-shrink-0" style={{ width: 220 }}>
          <div className="sticky top-24">
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'var(--font-caption)', marginBottom: 12 }}>
              On this page
            </p>
            <nav className="flex flex-col gap-0.5">
              {sections.map(s => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  style={{
                    fontSize: 13,
                    fontFamily: 'var(--font-caption)',
                    padding: '5px 10px',
                    borderRadius: 6,
                    color: activeSection === s.id ? '#2E7D32' : '#64748B',
                    background: activeSection === s.id ? 'rgba(46,125,50,0.08)' : 'transparent',
                    fontWeight: activeSection === s.id ? 600 : 400,
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'all 0.15s',
                  }}
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, minWidth: 0, maxWidth: 720 }}>

          {/* ── Introduction ── */}
          <SectionAnchor id="introduction" />
          <SectionTitle>Introduction</SectionTitle>

          <Para>
            {COMPANY} ("<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>") operates the website at <strong>{DOMAIN}</strong> and the associated web application (collectively, the "<strong>Service</strong>"). This Privacy Policy explains how we collect, use, disclose, and protect information about you when you use our Service.
          </Para>
          <Para>
            By creating an account or using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our practices, please do not use the Service.
          </Para>
          <InfoBox icon="Shield" title="Our commitment">
            We are committed to data minimisation. We only collect information that is directly necessary to provide the Service and we never sell your personal data to third parties.
          </InfoBox>

          {/* ── Information We Collect ── */}
          <SectionAnchor id="information" />
          <SectionTitle>Information We Collect</SectionTitle>

          <SubTitle>1. Information you provide directly</SubTitle>
          <BulletList items={[
            'Account registration: first name, last name, email address, and a hashed password.',
            'Profile updates: display name and notification preferences.',
            'Portfolio data: ticker symbols, share counts, purchase prices, and notes you enter manually.',
            'Payment information: processed exclusively by Stripe. We never store your card number, CVC, or full billing address on our servers.',
            'Support communications: messages you send to our support email.',
          ]} />

          <SubTitle>2. Information collected automatically</SubTitle>
          <BulletList items={[
            'Log data: IP address, browser type, operating system, referring URL, pages viewed, and timestamps.',
            'Device identifiers: browser fingerprint components used solely for fraud prevention.',
            'Usage data: feature interactions, session duration, and navigation paths — collected in aggregate and not linked to your identity.',
            'Authentication tokens: short-lived JWTs stored in your browser\'s localStorage for session management.',
          ]} />

          <SubTitle>3. Information from third-party services</SubTitle>
          <BulletList items={[
            'Brokerage data (SnapTrade): when you connect a brokerage account, SnapTrade provides us with read-only position, balance, and transaction data via OAuth. Your brokerage login credentials are never shared with us.',
            'Market & dividend data (Financial Modeling Prep): we retrieve publicly available dividend history, stock prices, and company profiles. No personal data is exchanged.',
            'Payment events (Stripe): we receive webhook events confirming subscription status, plan tier, and renewal dates.',
          ]} />

          {/* ── How We Use ── */}
          <SectionAnchor id="use" />
          <SectionTitle>How We Use Your Data</SectionTitle>

          <Para>We use the information we collect to:</Para>
          <BulletList items={[
            'Create and maintain your account and authenticate your sessions.',
            'Power the Service: calculate portfolio values, dividend income, DRIP projections, and alert conditions.',
            'Send transactional emails: account confirmation, password resets, payment receipts, and subscription notifications.',
            'Send optional email digests: weekly or monthly income summaries, if you opt in.',
            'Improve the Service: analyse aggregate usage patterns to identify bugs, prioritise features, and optimise performance.',
            'Enforce our Terms of Service and prevent fraud or abuse.',
            'Comply with applicable legal obligations.',
          ]} />

          <InfoBox icon="Lock" title="We do not use your data for advertising">
            We do not display advertisements, build advertising profiles, or share your data with advertising networks. Our only revenue comes from subscription fees.
          </InfoBox>

          {/* ── Sharing ── */}
          <SectionAnchor id="sharing" />
          <SectionTitle>Sharing &amp; Disclosure</SectionTitle>

          <Para>
            We do not sell, trade, or rent your personal information to third parties. We share data only in the following limited circumstances:
          </Para>

          <SubTitle>Service providers (sub-processors)</SubTitle>
          <div style={{ overflowX: 'auto', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#F1F5F9' }}>
                  {['Provider','Purpose','Data shared','Location'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#475569', fontFamily: 'var(--font-caption)', fontWeight: 600, border: '1px solid #E2E8F0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Stripe',               'Payment processing',              'Email, subscription plan',             'USA (SOC 2)'],
                  ['SnapTrade',            'Brokerage OAuth connector',       'User ID, portfolio sync token',        'Canada (SOC 2)'],
                  ['Financial Modeling Prep','Market & dividend data',        'Ticker symbols (no personal data)',    'USA'],
                  ['Brevo (Sendinblue)',   'Transactional email delivery',    'Email address, first name',            'EU (GDPR)'],
                  ['Hostinger',           'Web hosting & database',           'All encrypted account data',           'EU/USA'],
                ].map(([p,pu,d,l]) => (
                  <tr key={p} style={{ borderBottom: '1px solid #E2E8F0' }}>
                    {[p,pu,d,l].map((cell, i) => (
                      <td key={i} style={{ padding: '9px 14px', color: '#4B5563', border: '1px solid #E2E8F0', verticalAlign: 'top' }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SubTitle>Legal requirements</SubTitle>
          <Para>
            We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g. a court order or government agency), or where we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.
          </Para>

          <SubTitle>Business transfers</SubTitle>
          <Para>
            If {COMPANY} is involved in a merger, acquisition, or asset sale, your personal data may be transferred. We will provide notice before your data is transferred and becomes subject to a different privacy policy.
          </Para>

          {/* ── Retention ── */}
          <SectionAnchor id="retention" />
          <SectionTitle>Data Retention</SectionTitle>

          <Para>
            We retain your personal data for as long as your account is active or as needed to provide you the Service. Specific retention periods:
          </Para>
          <BulletList items={[
            'Active accounts: all data retained while your account is active.',
            'Cancelled accounts: account data retained for 60 days after cancellation to allow data export, then permanently deleted.',
            'Email logs: retained for 90 days for deliverability diagnostics.',
            'Financial transaction records: retained for 7 years as required by applicable financial record-keeping laws.',
            'Server access logs: retained for 30 days, then automatically purged.',
          ]} />
          <Para>
            You may request earlier deletion of your personal data at any time by contacting us at <a href={`mailto:${EMAIL_PRIVACY}`} style={{ color: '#2E7D32' }}>{EMAIL_PRIVACY}</a>.
          </Para>

          {/* ── Security ── */}
          <SectionAnchor id="security" />
          <SectionTitle>Security</SectionTitle>

          <Para>
            We take the security of your data seriously and implement industry-standard safeguards:
          </Para>
          <BulletList items={[
            'All data in transit is encrypted using TLS 1.2 or higher (HTTPS-only).',
            'Passwords are hashed using bcrypt with a cost factor of 12 — we never store plaintext passwords.',
            'Authentication uses short-lived JSON Web Tokens (JWT) with server-side expiry validation.',
            'Database credentials are stored in environment variables, never in source code.',
            'Brokerage connections via SnapTrade use OAuth tokens with read-only scope — no write access to your accounts.',
            'We conduct periodic security reviews and apply patches promptly.',
          ]} />

          <InfoBox icon="AlertTriangle" title="Important limitation">
            No method of transmission over the internet or electronic storage is 100% secure. While we use commercially reasonable means to protect your data, we cannot guarantee absolute security. In the event of a breach that affects your personal data, we will notify you as required by applicable law.
          </InfoBox>

          {/* ── Cookies ── */}
          <SectionAnchor id="cookies" />
          <SectionTitle>Cookies &amp; Tracking</SectionTitle>

          <SubTitle>What we use</SubTitle>
          <Para>
            We use a minimal set of browser storage mechanisms:
          </Para>
          <BulletList items={[
            'localStorage — stores your authentication token and user preferences (theme, default view). Cleared on logout.',
            'sessionStorage — not used.',
            'Cookies — we set one session cookie for CSRF protection. No advertising cookies are set.',
          ]} />

          <SubTitle>Third-party scripts</SubTitle>
          <Para>
            We do not load Google Analytics, Facebook Pixel, or any other third-party tracking scripts. The only third-party JavaScript loaded is Stripe.js (for payment processing), which is loaded directly from stripe.com.
          </Para>

          <SubTitle>Your choices</SubTitle>
          <Para>
            You can clear your localStorage and cookies at any time through your browser settings. Note that clearing authentication tokens will sign you out of the Service.
          </Para>

          {/* ── Your Rights ── */}
          <SectionAnchor id="rights" />
          <SectionTitle>Your Rights</SectionTitle>

          <Para>
            Depending on your jurisdiction, you may have the following rights regarding your personal data:
          </Para>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {[
              { icon: 'Eye',       title: 'Right to access',     desc: 'Request a copy of the personal data we hold about you.' },
              { icon: 'Edit',      title: 'Right to rectify',    desc: 'Correct inaccurate or incomplete personal data.' },
              { icon: 'Trash2',    title: 'Right to erasure',    desc: 'Request deletion of your data (subject to legal retention obligations).' },
              { icon: 'Pause',     title: 'Right to restrict',   desc: 'Ask us to restrict how we process your data in certain circumstances.' },
              { icon: 'Download',  title: 'Data portability',    desc: 'Receive your data in a structured, machine-readable format (CSV export).' },
              { icon: 'XCircle',   title: 'Right to object',     desc: 'Object to processing of your data for legitimate interests.' },
            ].map(r => (
              <div key={r.title} style={{ padding: '14px 16px', borderRadius: 10, border: '1px solid #E2E8F0', background: 'white' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Icon name={r.icon} size={15} color="#2E7D32" />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1E293B', fontFamily: 'var(--font-caption)' }}>{r.title}</span>
                </div>
                <p style={{ fontSize: 13, color: '#64748B', lineHeight: 1.6, margin: 0 }}>{r.desc}</p>
              </div>
            ))}
          </div>

          <Para>
            To exercise any of these rights, email us at <a href={`mailto:${EMAIL_PRIVACY}`} style={{ color: '#2E7D32' }}>{EMAIL_PRIVACY}</a>. We will respond within 30 days. We may need to verify your identity before processing your request.
          </Para>
          <Para>
            <strong>GDPR (European users):</strong> If you are located in the European Economic Area, you have additional rights under the General Data Protection Regulation, including the right to lodge a complaint with your national supervisory authority.
          </Para>
          <Para>
            <strong>CCPA (California residents):</strong> We do not sell personal information within the meaning of the California Consumer Privacy Act. California residents may submit requests under the CCPA to the email above.
          </Para>

          {/* ── Children ── */}
          <SectionAnchor id="children" />
          <SectionTitle>Children's Privacy</SectionTitle>

          <Para>
            The Service is not directed to individuals under 18 years of age. We do not knowingly collect personal information from anyone under 18. If we become aware that we have collected personal data from a child under 18 without parental consent, we will take steps to delete that information promptly.
          </Para>
          <Para>
            If you are a parent or guardian and believe your child has provided us with personal information, please contact us at <a href={`mailto:${EMAIL_PRIVACY}`} style={{ color: '#2E7D32' }}>{EMAIL_PRIVACY}</a>.
          </Para>

          {/* ── Changes ── */}
          <SectionAnchor id="changes" />
          <SectionTitle>Policy Changes</SectionTitle>

          <Para>
            We may update this Privacy Policy from time to time. When we make material changes, we will:
          </Para>
          <BulletList items={[
            'Update the "Effective date" at the top of this page.',
            'Send an email notification to all registered users at least 14 days before the changes take effect.',
            'Display a prominent notice in the app for 30 days following the change.',
          ]} />
          <Para>
            Your continued use of the Service after the effective date constitutes your acceptance of the updated policy. If you do not agree with the changes, you may delete your account before they take effect.
          </Para>

          {/* ── Contact ── */}
          <SectionAnchor id="contact" />
          <SectionTitle>Contact Us</SectionTitle>

          <Para>
            If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please reach out:
          </Para>

          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 14, padding: '24px 28px', marginTop: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1E293B', fontFamily: 'var(--font-heading)', marginBottom: 16 }}>{COMPANY}</div>
            <div className="flex flex-col gap-3">
              {[
                { icon: 'Mail',   label: 'Privacy inquiries', value: EMAIL_PRIVACY,  href: `mailto:${EMAIL_PRIVACY}` },
                { icon: 'Mail',   label: 'General support',   value: EMAIL_SUPPORT,  href: `mailto:${EMAIL_SUPPORT}` },
                { icon: 'Globe',  label: 'Website',           value: DOMAIN,          href: `https://${DOMAIN}` },
              ].map(c => (
                <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(46,125,50,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={c.icon} size={16} color="#2E7D32" />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#94A3B8', fontFamily: 'var(--font-caption)' }}>{c.label}</div>
                    <a href={c.href} style={{ fontSize: 14, color: '#2E7D32', fontFamily: 'var(--font-caption)', fontWeight: 500 }}>{c.value}</a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Para style={{ marginTop: 20 }}>
            We aim to respond to all privacy-related inquiries within 5 business days, and to all access, deletion, or correction requests within 30 calendar days.
          </Para>

          {/* Bottom nav */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-16 pt-8" style={{ borderTop: '1px solid #E2E8F0' }}>
            <Link to="/" style={{ fontSize: 14, color: '#2E7D32', fontFamily: 'var(--font-caption)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="ArrowLeft" size={15} color="#2E7D32" /> Back to Home
            </Link>
            <div className="flex gap-4">
              <Link to="/register" style={{ fontSize: 14, color: '#2E7D32', fontFamily: 'var(--font-caption)', fontWeight: 600 }}>Create Account</Link>
              <Link to="/login" style={{ fontSize: 14, color: '#64748B', fontFamily: 'var(--font-caption)' }}>Sign In</Link>
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer style={{ background: '#F1F5F9', borderTop: '1px solid #E2E8F0' }} className="px-6 py-8">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p style={{ fontSize: 13, color: '#94A3B8', fontFamily: 'var(--font-caption)' }}>
            © {new Date().getFullYear()} {COMPANY}. All rights reserved.
          </p>
          <div className="flex gap-6">
            {[['Privacy Policy','/privacy-policy'],['Terms of Service','/terms'],['Home','/']].map(([l,h]) => (
              <Link key={l} to={h} style={{ fontSize: 13, color: '#64748B', fontFamily: 'var(--font-caption)' }} className="hover:text-slate-900 transition-colors">{l}</Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
